// admin/src/features/preview/screenSize.ts
//
// How many CSS pixels make a real millimeter on the viewer's screen,
// without asking them to measure anything. Browsers never report physical
// size, but a screen's CSS width (window.screen.width) always spans the
// whole panel, whatever "looks like" scaling the OS uses. So identifying
// the panel gives the answer: CSS px per mm = screen width / panel width.
//
// Panels are matched by their known CSS resolutions (every macOS "looks
// like" option, iPad and iPhone point sizes, common monitors). Panel
// widths come from native pixels / ppi (Apple tech specs). Anything
// unrecognized falls back to the most likely panel for its resolution.

export type ScreenProbe = {
  /** window.screen.width / height (CSS px). */
  width: number;
  height: number;
  /** window.devicePixelRatio (includes browser zoom in Chrome/Firefox/Edge). */
  dpr: number;
  touch: boolean;
  apple: boolean;
};

export type ScreenMatch = {
  name: string;
  cssPxPerMm: number;
  /** Whether the panel was identified (vs. a best guess). */
  exact: boolean;
};

type Panel = { name: string; widthMm: number; modes: Array<[number, number]> };

const mm = (px: number, ppi: number) => (px / ppi) * 25.4;

/** Built-in and Apple displays, by every "looks like" resolution macOS offers. */
const MAC_PANELS: Panel[] = [
  { name: 'MacBook Pro 16″', widthMm: mm(3456, 254), modes: [[2056, 1329], [1728, 1117], [1496, 967], [1312, 848], [1168, 755]] },
  { name: 'MacBook Pro 14″', widthMm: mm(3024, 254), modes: [[1800, 1169], [1512, 982], [1352, 878], [1147, 745], [1024, 665]] },
  { name: 'MacBook Air 15″', widthMm: mm(2880, 224), modes: [[1920, 1243], [1920, 1248], [1710, 1107], [1680, 1087], [1440, 932], [1280, 828]] },
  { name: 'MacBook Air 13″', widthMm: mm(2560, 224), modes: [[1710, 1112], [1470, 956], [1280, 832], [1024, 666], [1024, 665]] },
  { name: 'MacBook 13″', widthMm: mm(2560, 227), modes: [[1680, 1050], [1440, 900], [1280, 800], [1024, 640]] },
  { name: 'MacBook Pro 16″ (Intel)', widthMm: mm(3072, 226), modes: [[2048, 1280], [1792, 1120], [1536, 960], [1344, 840], [1152, 720]] },
  { name: 'iMac 24″', widthMm: mm(4480, 218), modes: [[2240, 1260], [2048, 1152], [1600, 900]] },
  { name: 'Studio Display', widthMm: mm(5120, 218), modes: [[3200, 1800], [2880, 1620], [2560, 1440]] },
  { name: 'Pro Display XDR', widthMm: mm(6016, 218), modes: [[3008, 1692], [3384, 1903], [3760, 2115]] },
];

/** iPads by portrait point width: 264 ppi at 2x, except the mini (326 ppi). */
const IPADS: Array<{ name: string; width: number; ppi: number }> = [
  { name: 'iPad mini', width: 744, ppi: 326 },
  { name: 'iPad mini', width: 768, ppi: 326 },
  { name: 'iPad', width: 810, ppi: 264 },
  { name: 'iPad Air 11″', width: 820, ppi: 264 },
  { name: 'iPad Pro 11″', width: 834, ppi: 264 },
  { name: 'iPad Pro 13″', width: 1024, ppi: 264 },
  { name: 'iPad Pro 13″', width: 1032, ppi: 264 },
];

/** Common desktop monitors at 1x, by CSS width x height. */
const MONITORS: Array<{ name: string; width: number; height: number; widthMm: number }> = [
  { name: '24″ monitor', width: 1920, height: 1080, widthMm: 527 },
  { name: '24″ monitor', width: 1920, height: 1200, widthMm: 518 },
  { name: '27″ monitor', width: 2560, height: 1440, widthMm: 597 },
  { name: '29″ ultrawide', width: 2560, height: 1080, widthMm: 673 },
  { name: '34″ ultrawide', width: 3440, height: 1440, widthMm: 800 },
  { name: '32″ 4K monitor', width: 3840, height: 2160, widthMm: 698 },
  { name: '22″ monitor', width: 1680, height: 1050, widthMm: 474 },
];

function landscape(w: number, h: number): [number, number] {
  return w >= h ? [w, h] : [h, w];
}

function findMac(w: number, h: number): Panel | null {
  return MAC_PANELS.find((p) => p.modes.some(([mw, mh]) => Math.abs(mw - w) <= 1 && Math.abs(mh - h) <= 1)) ?? null;
}

/**
 * CSS px per mm at the current zoom. `osDpr` is the display's own scale;
 * any extra devicePixelRatio is browser zoom, which makes each CSS px
 * physically bigger.
 */
function atZoom(cssWidth: number, widthMm: number, osDpr: number, dpr: number): number {
  return (cssWidth / widthMm) * (osDpr / dpr);
}

export function detectScreen(probe: ScreenProbe): ScreenMatch {
  const { dpr, touch, apple } = probe;
  const short = Math.min(probe.width, probe.height);

  // Phones: CSS px are a fixed physical size per platform.
  if (touch && short <= 500) {
    if (apple) {
      return dpr >= 3 ? { name: 'iPhone', cssPxPerMm: 460 / 3 / 25.4, exact: true } : { name: 'iPhone', cssPxPerMm: 326 / 2 / 25.4, exact: true };
    }
    return { name: 'Android phone', cssPxPerMm: 160 / 25.4, exact: false };
  }

  // iPads (Safari reports a Mac user agent, but with touch).
  if (apple && touch) {
    const ipad = IPADS.find((p) => p.width === short);
    const ppi = ipad?.ppi ?? 264;
    return { name: ipad?.name ?? 'iPad', cssPxPerMm: ppi / 2 / 25.4, exact: Boolean(ipad) };
  }

  const [w, h] = landscape(probe.width, probe.height);

  // Retina Macs (2x). Chrome and Safari keep screen.width at the "looks like"
  // size under zoom; Firefox divides it by the zoom, so also try it unzoomed.
  if (apple && dpr >= 1.5) {
    for (const zoomed of [1, dpr / 2]) {
      const cw = Math.round(w * zoomed);
      const panel = findMac(cw, Math.round(h * zoomed));
      if (panel) return { name: panel.name, cssPxPerMm: atZoom(cw, panel.widthMm, 2, dpr), exact: true };
    }
  }

  const monitor = MONITORS.find((m) => m.width === w && m.height === h);
  if (monitor) return { name: monitor.name, cssPxPerMm: w / monitor.widthMm, exact: dpr === 1 };

  // Unknown laptop (scaled OS, e.g. Windows 125-150%): assume a 14″ 16:9 panel.
  if (dpr > 1 && w <= 1920) return { name: 'Laptop', cssPxPerMm: w / 310, exact: false };
  // Unknown desktop: assume ~0.25 mm pixels (a 24-27″ class monitor).
  return { name: 'Monitor', cssPxPerMm: 4, exact: false };
}

export function probeScreen(): ScreenProbe {
  const ua = navigator.userAgent;
  return {
    width: window.screen.width,
    height: window.screen.height,
    dpr: window.devicePixelRatio || 1,
    touch: navigator.maxTouchPoints > 1,
    apple: /Macintosh|Mac OS X|iPhone|iPad/.test(ua),
  };
}
